import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, MenuItem, Paper, Skeleton, Stack, Tab, Tabs, TextField, Typography, } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { createCaseWithNewPatient, getAllCases } from '../supabase/crud';
import { getColorForLetter } from './avatarColor';
import { initialOf, toTitleCase } from './textFormat';
const HAIRLINE = 'rgba(15, 23, 42, 0.08)';
const HAIRLINE_SOFT = 'rgba(15, 23, 42, 0.06)';
// Avatars live in their own fixed-width column (like ListItemAvatar's
// default) so they form a clean vertical line regardless of name length —
// only the name TEXT lives in the centered column below.
const AVATAR_COL_WIDTH = 56;
// Fixed-width name-text column so the "Name" header label and each row's
// name text share the exact same center-x, regardless of how long any
// given name is. A flexible spacer after it pushes the date column to the
// far right.
const NAME_COL_WIDTH = 220;
// Fixed-width date column so the "Date" header label and each row's date
// value share the same left edge (a left-aligned column, not flush-right).
const DATE_COL_WIDTH = 112;
// Search is temporarily hidden per request; filtering logic stays wired up
// so it can be switched back on later without rework.
const SHOW_SEARCH = false;
const STAGES = ['New', 'In Progress', 'Completed'];
const STAGE_SLUGS = {
    New: 'new',
    'In Progress': 'in-progress',
    Completed: 'completed',
};
const STAGE_BY_SLUG = {
    new: 'New',
    'in-progress': 'In Progress',
    completed: 'Completed',
};
// "All" is deliberately not part of this list — it isn't one more stage
// filter in the same group, it's a separate "clear the filter" affordance,
// and is rendered on its own, pushed to the far right of the tab bar.
const FILTER_TABS = [
    { label: 'New', value: 'New' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Completed', value: 'Completed' },
];
const EMPTY_FORM = {
    firstName: '',
    lastName: '',
    rijksregister: '',
    mutualiteit: '',
    email: '',
    contact: '',
    rijksregisterMoeder: '',
    stage: 'New',
    status: '',
    status1: '',
    deadline: '',
    submittedOn: '',
    received: '',
    amount: '',
    paid: '',
    retroactive: '',
    monthly: '',
    tags: '',
};
const formatCreatedDate = (iso) => {
    if (!iso)
        return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return undefined;
    return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};
const numOrUndefined = (s) => {
    const trimmed = s.trim();
    if (!trimmed)
        return undefined;
    const n = Number(trimmed);
    return Number.isNaN(n) ? undefined : n;
};
const strOrUndefined = (s) => (s.trim() ? s.trim() : undefined);
// Always shows a sort-direction arrow so both columns read as sortable at a
// glance. The arrow is rendered in the exact same color on both columns at
// all times — there is no active/selected vs. inactive color distinction on
// the icon itself (only its direction changes, for the column actually
// being sorted).
function SortArrow({ active, dir }) {
    const Icon = active && dir === 'desc' ? ArrowDownwardRoundedIcon : ArrowUpwardRoundedIcon;
    return _jsx(Icon, { sx: { fontSize: 14, color: 'text.secondary' } }, void 0);
}
export default function CaseList() {
    const { stage: stageSlug } = useParams();
    const stageFilter = (stageSlug && STAGE_BY_SLUG[stageSlug]) || 'all';
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const toggleSort = (column) => {
        if (sortBy === column) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        }
        else {
            setSortBy(column);
            setSortDir('asc');
        }
    };
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const fetchCases = async () => {
        try {
            const { cases } = await getAllCases();
            setCases(cases);
            setError(false);
        }
        catch (err) {
            console.error('Error fetching cases:', err);
            setError(true);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchCases();
    }, []);
    const handleRetry = () => {
        setLoading(true);
        fetchCases();
    };
    const closeDialog = () => {
        if (saving)
            return;
        setDialogOpen(false);
        setForm(EMPTY_FORM);
    };
    const handleAddCase = async () => {
        if (!form.firstName.trim() || !form.lastName.trim())
            return;
        setSaving(true);
        try {
            const tags = form.tags
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean);
            await createCaseWithNewPatient({
                email: strOrUndefined(form.email),
                contact: strOrUndefined(form.contact),
                rijksregisterMoeder: strOrUndefined(form.rijksregisterMoeder),
            }, {
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                rijksregister: strOrUndefined(form.rijksregister),
                mutualiteit: strOrUndefined(form.mutualiteit),
            }, {
                stage: form.stage,
                status: strOrUndefined(form.status),
                status1: strOrUndefined(form.status1),
                deadline: strOrUndefined(form.deadline),
                submittedOn: strOrUndefined(form.submittedOn),
                received: strOrUndefined(form.received),
                amount: numOrUndefined(form.amount),
                paid: numOrUndefined(form.paid),
                retroactive: numOrUndefined(form.retroactive),
                monthly: numOrUndefined(form.monthly),
                tags: tags.length ? tags : undefined,
            });
            setForm(EMPTY_FORM);
            setDialogOpen(false);
            await fetchCases();
        }
        catch (err) {
            console.error('Error adding case:', err);
        }
        finally {
            setSaving(false);
        }
    };
    const sortedCases = useMemo(() => {
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...cases].sort((a, b) => {
            if (sortBy === 'date') {
                const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return (aTime - bTime) * dir;
            }
            return a.patient.firstName.localeCompare(b.patient.firstName) * dir;
        });
    }, [cases, sortBy, sortDir]);
    const stageFilteredCases = useMemo(() => {
        if (stageFilter === 'all')
            return sortedCases;
        return sortedCases.filter((c) => c.stage === stageFilter);
    }, [sortedCases, stageFilter]);
    const filteredCases = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query)
            return stageFilteredCases;
        return stageFilteredCases.filter((c) => `${c.patient.firstName} ${c.patient.lastName}`.toLowerCase().includes(query));
    }, [stageFilteredCases, search]);
    const previewInitials = `${form.firstName[0] ?? ''}${form.lastName[0] ?? ''}`.toUpperCase();
    return (_jsxs(Box, { sx: {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.default',
        }, children: [_jsxs(Box, { sx: {
                    flexShrink: 0,
                    width: '80%',
                    maxWidth: 1400,
                    mx: 'auto',
                    px: 4,
                    pt: 5,
                    pb: 3,
                }, children: [_jsxs(Stack, { direction: "row", sx: {
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 3.5,
                        }, children: [_jsx(Stack, { direction: "row", spacing: 1.5, sx: { alignItems: 'center' }, children: _jsx(Typography, { variant: "h4", children: "Cases" }, void 0) }, void 0), _jsx(Button, { variant: "contained", disableElevation: true, startIcon: _jsx(AddRoundedIcon, { sx: { fontSize: 20 } }, void 0), onClick: () => setDialogOpen(true), sx: {
                                    px: 3,
                                    py: 1.2,
                                    fontSize: 14.5,
                                    fontWeight: 700,
                                    letterSpacing: 0.2,
                                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.16)',
                                    '&:hover': { boxShadow: '0 1px 3px rgba(15, 23, 42, 0.22)' },
                                }, children: "Add Case" }, void 0)] }, void 0), SHOW_SEARCH && (_jsx(TextField, { size: "small", placeholder: "Search cases by patient name", value: search, onChange: (e) => setSearch(e.target.value), sx: {
                            width: '100%',
                            maxWidth: 340,
                            bgcolor: 'background.paper',
                            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                        }, slotProps: {
                            input: {
                                startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchRoundedIcon, { fontSize: "small", color: "disabled" }, void 0) }, void 0)),
                            },
                        } }, void 0)), _jsxs(Box, { sx: {
                            mt: 2.5,
                            minHeight: 46,
                            display: 'flex',
                            alignItems: 'stretch',
                            borderBottom: '1px solid',
                            borderColor: HAIRLINE,
                        }, children: [_jsx(Tabs, { value: stageFilter === 'all' ? false : stageFilter, onChange: (_e, value) => navigate(`/cases/${STAGE_SLUGS[value]}`), slotProps: {
                                    indicator: {
                                        sx: {
                                            height: 2.5,
                                            borderRadius: '2px 2px 0 0',
                                            backgroundColor: 'primary.main',
                                        },
                                    },
                                }, sx: {
                                    minHeight: 46,
                                    '& .MuiTabs-flexContainer': { gap: 1 },
                                    '& .MuiTab-root': {
                                        minHeight: 46,
                                        minWidth: 96,
                                        px: 3,
                                        py: 0,
                                        fontSize: 15.5,
                                        fontWeight: 600,
                                        letterSpacing: 0.1,
                                        textTransform: 'none',
                                        color: '#5B6775',
                                        transition: 'color 0.15s ease',
                                    },
                                    '& .MuiTab-root:hover': { color: '#2B3542' },
                                    '& .MuiTab-root.Mui-selected': {
                                        color: 'primary.main',
                                        fontWeight: 700,
                                    },
                                }, children: FILTER_TABS.map(({ label, value }) => (_jsx(Tab, { disableRipple: true, label: label, value: value }, value))) }, void 0), _jsx(Box, { sx: { flex: 1 } }, void 0), _jsx(ButtonBase, { disableRipple: true, onClick: () => navigate('/cases'), sx: {
                                    px: 2.5,
                                    borderLeft: '1px solid',
                                    borderColor: HAIRLINE,
                                    fontSize: 14.5,
                                    fontWeight: stageFilter === 'all' ? 700 : 600,
                                    letterSpacing: 0.1,
                                    color: stageFilter === 'all' ? 'primary.main' : '#8996A6',
                                    bgcolor: stageFilter === 'all' ? 'rgba(0, 115, 188, 0.08)' : 'transparent',
                                    transition: 'color 0.15s ease, background-color 0.15s ease',
                                    '&:hover': stageFilter === 'all' ? undefined : { color: '#2B3542' },
                                }, children: "All" }, void 0)] }, void 0)] }, void 0), _jsx(Box, { sx: {
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    px: 4,
                    pb: 5,
                    display: 'flex',
                    flexDirection: 'column',
                }, children: _jsx(Box, { sx: {
                        width: '80%',
                        maxWidth: 1400,
                        mx: 'auto',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }, children: _jsxs(Paper, { variant: "outlined", sx: {
                            borderRadius: '8px',
                            borderColor: HAIRLINE,
                            maxHeight: '100%',
                            overflow: 'auto',
                        }, children: [!error && !loading && filteredCases.length > 0 && (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.25,
                                    borderBottom: '1px solid',
                                    borderColor: HAIRLINE_SOFT,
                                    bgcolor: '#F2F2F5',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                }, children: [_jsx(Box, { sx: { width: AVATAR_COL_WIDTH, flexShrink: 0 } }, void 0), _jsxs(ButtonBase, { disableRipple: true, onClick: () => toggleSort('name'), sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.5,
                                            width: NAME_COL_WIDTH,
                                            flexShrink: 0,
                                        }, children: [_jsx(Typography, { variant: "caption", sx: {
                                                    fontWeight: 700,
                                                    color: 'text.primary',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.4,
                                                }, children: "Name" }, void 0), _jsx(SortArrow, { active: sortBy === 'name', dir: sortDir }, void 0)] }, void 0), _jsx(Box, { sx: { flex: 1 } }, void 0), _jsxs(ButtonBase, { disableRipple: true, onClick: () => toggleSort('date'), sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            gap: 0.5,
                                            width: DATE_COL_WIDTH,
                                            flexShrink: 0,
                                        }, children: [_jsx(Typography, { variant: "caption", sx: {
                                                    fontWeight: 700,
                                                    color: 'text.primary',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.4,
                                                }, children: "Date" }, void 0), _jsx(SortArrow, { active: sortBy === 'date', dir: sortDir }, void 0)] }, void 0)] }, void 0)), error ? (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    py: 8,
                                    px: 3,
                                }, children: [_jsx(Box, { sx: {
                                            width: 56,
                                            height: 56,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                                            mb: 1,
                                        }, children: _jsx(ErrorOutlineRoundedIcon, { sx: { fontSize: 26, color: 'error.main' } }, void 0) }, void 0), _jsx(Typography, { sx: { fontWeight: 600 }, children: "Couldn't load cases" }, void 0), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "Something went wrong while fetching your case list." }, void 0), _jsx(Button, { size: "small", variant: "outlined", startIcon: _jsx(RefreshRoundedIcon, {}, void 0), onClick: handleRetry, children: "Try again" }, void 0)] }, void 0)) : loading ? (_jsx(List, { disablePadding: true, children: Array.from({ length: 6 }).map((_, i) => (_jsxs(ListItem, { divider: i < 5, sx: { py: 1.5 }, children: [_jsx(ListItemAvatar, { children: _jsx(Skeleton, { variant: "circular", width: 38, height: 38 }, void 0) }, void 0), _jsx(ListItemText, { primary: _jsx(Skeleton, { width: "35%" }, void 0) }, void 0)] }, i))) }, void 0)) : filteredCases.length === 0 ? (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    py: 8,
                                    px: 3,
                                }, children: [_jsx(Box, { sx: {
                                            width: 56,
                                            height: 56,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: HAIRLINE_SOFT,
                                            mb: 1,
                                        }, children: _jsx(PeopleAltRoundedIcon, { sx: { fontSize: 26, color: 'text.disabled' } }, void 0) }, void 0), _jsx(Typography, { sx: { fontWeight: 600 }, children: cases.length === 0
                                            ? 'No cases yet'
                                            : search.trim()
                                                ? 'No matching cases'
                                                : `No ${stageFilter} cases` }, void 0), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: cases.length === 0 ? 1 : 0 }, children: cases.length === 0
                                            ? 'Add your first case to get started.'
                                            : search.trim()
                                                ? 'Try a different search.'
                                                : 'Try a different tab.' }, void 0), cases.length === 0 && (_jsx(Button, { size: "small", startIcon: _jsx(AddRoundedIcon, {}, void 0), onClick: () => setDialogOpen(true), children: "Add Case" }, void 0))] }, void 0)) : (_jsx(List, { disablePadding: true, children: filteredCases.map((c, i) => (_jsx(ListItem, { disablePadding: true, divider: i < filteredCases.length - 1, sx: { borderColor: HAIRLINE_SOFT }, children: _jsxs(ListItemButton, { disableRipple: true, disableTouchRipple: true, onClick: () => navigate(`/case/${c.id}`), sx: {
                                            py: 1.65,
                                            transition: 'background-color 0.12s ease',
                                            '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.035)' },
                                        }, children: [_jsx(ListItemAvatar, { sx: { minWidth: AVATAR_COL_WIDTH }, children: _jsxs(Avatar, { sx: {
                                                        width: 33,
                                                        height: 33,
                                                        bgcolor: getColorForLetter(c.patient.firstName[0]),
                                                        fontWeight: 700,
                                                        fontSize: 12.5,
                                                        letterSpacing: 0.2,
                                                        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.08)',
                                                    }, children: [initialOf(c.patient.firstName), initialOf(c.patient.lastName)] }, void 0) }, void 0), _jsx(Box, { sx: {
                                                    width: NAME_COL_WIDTH,
                                                    flexShrink: 0,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    minWidth: 0,
                                                }, children: _jsxs(Typography, { sx: {
                                                        fontWeight: 600,
                                                        fontSize: 14.5,
                                                        letterSpacing: 0.1,
                                                        maxWidth: '100%',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }, children: [toTitleCase(c.patient.firstName), " ", toTitleCase(c.patient.lastName)] }, void 0) }, void 0), _jsx(Box, { sx: { flex: 1 } }, void 0), _jsx(Typography, { variant: "body2", sx: {
                                                    color: 'text.secondary',
                                                    width: DATE_COL_WIDTH,
                                                    flexShrink: 0,
                                                    textAlign: 'left',
                                                    fontSize: 13.5,
                                                    letterSpacing: 0.1,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }, children: formatCreatedDate(c.createdAt) }, void 0)] }, void 0) }, c.id))) }, void 0))] }, void 0) }, void 0) }, void 0), _jsxs(Dialog, { open: dialogOpen, onClose: closeDialog, fullWidth: true, maxWidth: "sm", slotProps: { paper: { sx: { borderRadius: '10px' } } }, children: [_jsx(DialogTitle, { sx: { fontWeight: 700 }, children: "Add case" }, void 0), _jsx(Divider, {}, void 0), _jsxs(DialogContent, { children: [_jsxs(Stack, { direction: "row", spacing: 2, sx: { alignItems: 'center', pt: 2.5, pb: 3 }, children: [_jsx(Avatar, { sx: {
                                            width: 52,
                                            height: 52,
                                            fontWeight: 700,
                                            fontSize: 16,
                                            bgcolor: form.firstName
                                                ? getColorForLetter(form.firstName[0])
                                                : HAIRLINE_SOFT,
                                            color: form.firstName ? '#fff' : 'text.disabled',
                                            transition: 'background-color 0.15s ease',
                                        }, children: previewInitials || _jsx(PeopleAltRoundedIcon, { sx: { fontSize: 20 } }, void 0) }, void 0), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: 14.5 }, children: form.firstName || form.lastName
                                                    ? `${toTitleCase(form.firstName)} ${toTitleCase(form.lastName)}`.trim()
                                                    : 'New patient' }, void 0), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "A client record is created automatically for this patient" }, void 0)] }, void 0)] }, void 0), _jsxs(Box, { component: "form", id: "add-case-form", onSubmit: (e) => {
                                    e.preventDefault();
                                    handleAddCase();
                                }, sx: { display: 'flex', flexDirection: 'column', gap: 3 }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Patient" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { autoFocus: true, label: "First name", value: form.firstName, onChange: updateField('firstName'), fullWidth: true }, void 0), _jsx(TextField, { label: "Last name", value: form.lastName, onChange: updateField('lastName'), fullWidth: true }, void 0)] }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Rijksregister", value: form.rijksregister, onChange: updateField('rijksregister'), fullWidth: true }, void 0), _jsx(TextField, { label: "Mutualiteit", value: form.mutualiteit, onChange: updateField('mutualiteit'), fullWidth: true }, void 0)] }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Client (mother/contact)" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Email", value: form.email, onChange: updateField('email'), fullWidth: true }, void 0), _jsx(TextField, { label: "Contact", value: form.contact, onChange: updateField('contact'), fullWidth: true }, void 0)] }, void 0), _jsx(TextField, { label: "Rijksregister (moeder)", value: form.rijksregisterMoeder, onChange: updateField('rijksregisterMoeder'), fullWidth: true }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Case" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { select: true, label: "Stage", value: form.stage, onChange: updateField('stage'), fullWidth: true, children: STAGES.map((s) => (_jsx(MenuItem, { value: s, children: s }, s))) }, void 0), _jsx(TextField, { label: "Status", value: form.status, onChange: updateField('status'), fullWidth: true }, void 0)] }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Status (secondary)", value: form.status1, onChange: updateField('status1'), fullWidth: true }, void 0), _jsx(TextField, { label: "Deadline", type: "date", value: form.deadline, onChange: updateField('deadline'), slotProps: { inputLabel: { shrink: true } }, fullWidth: true }, void 0)] }, void 0), _jsx(TextField, { label: "Tags (comma separated)", value: form.tags, onChange: updateField('tags'), placeholder: "herziening, spraakcomputer", fullWidth: true }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Financial" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Amount", type: "number", value: form.amount, onChange: updateField('amount'), fullWidth: true }, void 0), _jsx(TextField, { label: "Paid", type: "number", value: form.paid, onChange: updateField('paid'), fullWidth: true }, void 0)] }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Retroactive", type: "number", value: form.retroactive, onChange: updateField('retroactive'), fullWidth: true }, void 0), _jsx(TextField, { label: "Monthly", type: "number", value: form.monthly, onChange: updateField('monthly'), fullWidth: true }, void 0)] }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Tracking" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Submitted on", type: "date", value: form.submittedOn, onChange: updateField('submittedOn'), slotProps: { inputLabel: { shrink: true } }, fullWidth: true }, void 0), _jsx(TextField, { label: "Received", type: "date", value: form.received, onChange: updateField('received'), slotProps: { inputLabel: { shrink: true } }, fullWidth: true }, void 0)] }, void 0)] }, void 0)] }, void 0)] }, void 0), _jsxs(DialogActions, { sx: { px: 3, pb: 2 }, children: [_jsx(Button, { onClick: closeDialog, disabled: saving, color: "inherit", children: "Cancel" }, void 0), _jsx(Button, { type: "submit", form: "add-case-form", variant: "contained", disableElevation: true, disabled: saving || !form.firstName.trim() || !form.lastName.trim(), startIcon: saving ? (_jsx(CircularProgress, { size: 16, color: "inherit" }, void 0)) : undefined, children: saving ? 'Saving…' : 'Save' }, void 0)] }, void 0)] }, void 0)] }, void 0));
}
